import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col } from "react-bootstrap";
import { IActionHandler, WebsocketCollectionServerMessage, MessageDisplayType, ActionHandlerFunction, RecoveryAccountCredentials } from "@preveil-api";
import {
  WebsocketConnectionRoutes, useAppDispatch, useAppSelector, instanceofKeyTransfer, MessageHandlerDisplayType, KeyExchangeManager, TransferAccountSteps, TransferAccountActions, TransferAccountTypes, Message,
  AccountErrorMessages, GlobalErrorMessages, MessageAnchors, ConfigKeys, AccountUIActionTypes, instanceofRecoveryCredentials, Helpers, ExpressResetPasswordUIActionTypes
} from "src/common";
import { Loading } from "src/components";
import { AwaitConnection, ConnectionEstablished } from ".";
import { uiActions, websocketActions, WebsocketStatus, getConfigValue } from "src/store";
import { RootState } from "src/store/configureStore";
import connect_img from "src/assets/images/copy_account.svg";

type RecoveryKeyTransferPayload = {
  device_req: string;
  message: string;
  signature: string;
};

type AllProps = {
  user_id: string;
  approver_user_id: string;
  handleAction: ActionHandlerFunction;
  recovery_key_exchange_manager?: KeyExchangeManager;
  recovery_key_transfer?: RecoveryKeyTransferPayload;
}

interface PublicKeyHashType {
  protocol_version: number;
  public_key_hash: string;
  other_public_key_hash?: string;
}

function GetShard(props: AllProps) {
  const { user_id, approver_user_id, handleAction, recovery_key_exchange_manager, recovery_key_transfer } = props;
  const dispatch = useAppDispatch();
  const message = useAppSelector((state: RootState) => state.websocket.message as WebsocketCollectionServerMessage);
  const status = useAppSelector((state: RootState) => state.websocket.status);
  const [step, setStep] = useState<number>(TransferAccountSteps.DISCONNECTED);
  const [pin, setPin] = useState<string>();
  const [protocol, setProtocol] = useState<number>(0);
  const [key_exchange_manager, setKeyExchangeManager] = useState<KeyExchangeManager>();
  const backend_websocket_server = getConfigValue(ConfigKeys.backend_websocket_server);
  const public_key_hash_ref = useRef<PublicKeyHashType | null>(null);

  useEffect(() => {
    handleInitConnectAccount();
    return () => {  // Destroy websocket State on leave
      dispatch(websocketActions.destroyWebsocket());
    };
  }, []);

  // Description: Responds to changes in message and websocket status
  useEffect(() => {
    const next = step + 1;
    setStep(status === WebsocketStatus.disconnected ? TransferAccountSteps.DISCONNECTED :
      (!!message && !!message.data) ? handleMapSteps(message.data, next) : next);
  }, [message, status]);

  // Description: Analyzes incoming message and current step 
  // Handle action steps based on server responses
  function handleMapSteps(data: any, next: number): number {
    const current_step = !!data.accept_protocols ? TransferAccountSteps.SEND_ACTION_CONNECT :
      instanceofKeyTransfer(data) ? TransferAccountSteps.SEND_KEY_TRANSFER :
        instanceofRecoveryCredentials(data) ? TransferAccountSteps.SEND_COMPLETE_LOGIN :
          !!data.user_id ? TransferAccountSteps.SET_PIN_CONFIRMATION_KEY_TRANSFER : next;

    //  STEP 3 : receives messages: with accept_protocols and 
    if (current_step === TransferAccountSteps.SEND_ACTION_CONNECT) {
      DeviceRequests.handleConnectMessage(data.accept_protocols);
    }

    // STEP 4 : DeviceRequests.handleKeyTransferMessage - Send public key hash to server "action": "key_transfer"
    if (current_step === TransferAccountSteps.SEND_KEY_TRANSFER) {
      const _public_key_hash = { public_key_hash: data.public_key_hash, protocol_version: data.protocol_version };
      public_key_hash_ref.current = _public_key_hash;
      DeviceRequests.handleKeyTransferMessage(_public_key_hash);
    }

    // STEP 5: Sets Pin change Screen
    if (current_step === TransferAccountSteps.SET_PIN_CONFIRMATION_KEY_TRANSFER && !!data.message) {
      const _public_key_hash = Object.assign({}, public_key_hash_ref.current, { other_public_key_hash: data.message });
      public_key_hash_ref.current = _public_key_hash;
      DeviceRequests.handleVerifyHashCompleteHandshake(_public_key_hash);
    }

    // LOGIN WITH data
    if (current_step === TransferAccountSteps.SEND_COMPLETE_LOGIN) {
      DeviceRequests.handleSuccess(data);
    }

    return current_step;
  }

  function handleInitConnectAccount() {
    if (!!recovery_key_exchange_manager) {
      setKeyExchangeManager(recovery_key_exchange_manager);
    } else {
      setKeyExchangeManager(KeyExchangeManager.initKeyExchangeManager());
    }
    DeviceRequests.handleInitWebsocketConnection();
  }

  // Description: Render forms conditionally
  function ConnectAccountPanels() {
    switch (step) {
      case TransferAccountSteps.ERROR:
        return <p> Error </p>;
      case TransferAccountSteps.DISCONNECTED:
      case TransferAccountSteps.INITIALIZE_CONNECTION:
      case TransferAccountSteps.GET_ACCEPTED_PROTOCOL_VERSIONS:
      case TransferAccountSteps.SEND_ACTION_CONNECT: // send: action: "connect" ...
      case TransferAccountSteps.GET_HASH:  // gets data: protocol_version, public_key_hash
      case TransferAccountSteps.GET_PUBLIC_KEY: // gets data: message, user_id
      case TransferAccountSteps.SEND_KEY_TRANSFER: // gets data: message, user_id
        return <>
          <Card.Img variant="top" src={connect_img} />
          <Card.Body>
            <AwaitConnection handleAction={handlePageActions} />
          </Card.Body>
        </>;
      case TransferAccountSteps.SET_PIN_CONFIRMATION_KEY_TRANSFER:
        return <Card.Body className="p-4">
          {!!pin ? <ConnectionEstablished pin={pin} handleAction={handlePageActions} /> : <Loading className="in-place" />}
        </Card.Body>;
      default:
        return <Loading className="in-place" />;
    }
  }

  const DeviceRequests = {
    // Description: Initialize the connection
    handleInitWebsocketConnection: () => {
      if (!!user_id) {
        const host = `${backend_websocket_server}/${WebsocketConnectionRoutes.relay_ws}`;
        const init = {
          action: TransferAccountActions.ConnectHandshake,
          type: TransferAccountTypes.Recovery,
          accept_protocols: KeyExchangeManager.supported_protocols,
          handshake_protocol: KeyExchangeManager.handshake_protocol,
          user_id,
          connect_to: approver_user_id,
        };
        dispatch(websocketActions.wsConnect({ host, init }));
      } else {
        DeviceRequests.handlePageErrorMessage({ message: AccountErrorMessages.default, stack: { error: AccountErrorMessages.missing_user_id } });
      }
    },

    // Description: Send Step 2 message after Accepted_protocols has been returned from initial Handshake
    handleConnectMessage: (accepted_protocols: number[]) => {
      if (!!key_exchange_manager) {
        key_exchange_manager.getProtocol(accepted_protocols).then((protocol: number) => {
          setProtocol(protocol);
          key_exchange_manager.getPublicKeyHash()
            .then((public_key_hash: string) => {
              const params = {
                action: TransferAccountActions.Connect,
                type: TransferAccountTypes.Recovery,
                protocol_version: KeyExchangeManager.current_protocol,
                public_key_hash,
                user_id
              };
              dispatch(websocketActions.wsOnSendMessage(JSON.stringify(params)));
            });
        });
      } else {
        DeviceRequests.handlePageErrorMessage({
          message: AccountErrorMessages.default,
          stack: { error: AccountErrorMessages.kss_undefined }
        });
      }
    },

    // Description: Send Step 3 message after public_key_hash and other_public_key has been returned from step 2
    handleKeyTransferMessage: (_public_key_hash: PublicKeyHashType): void => {
      if (!!key_exchange_manager) {
        const sendKeyTransfer = (device_req: string, message: string, signature: string) => {
          const params = {
            action: TransferAccountActions.KeyTransfer,
            user_id,
            device_req,
            message,
            signature
          };
          dispatch(websocketActions.wsOnSendMessage(JSON.stringify(params)));
        };
        if (!!recovery_key_transfer) {
          const { device_req, message, signature } = recovery_key_transfer;
          sendKeyTransfer(device_req, message, signature);
        } else {
          key_exchange_manager.constructSerializedRecoveryData(user_id)
            .then((_public_key_object: { device_req: string; message: string; signature: string }) => {
              const { device_req, message, signature } = _public_key_object;
              sendKeyTransfer(device_req, message, signature);
            });
        }
      } else {
        DeviceRequests.handlePageErrorMessage({
          message: AccountErrorMessages.default,
          stack: { error: AccountErrorMessages.kss_transfer_undefined }
        });
      }
    },

    // Description: Verify hash and complete handshake
    handleVerifyHashCompleteHandshake: (public_key_hash_obj: PublicKeyHashType): void => {
      const { public_key_hash, other_public_key_hash } = public_key_hash_obj;
      if (!!key_exchange_manager && !!other_public_key_hash && !!public_key_hash && protocol > 0) {
        key_exchange_manager.verifyHashAndCompleteHandshake(other_public_key_hash, public_key_hash, protocol)
          .then((_pin: string | null) => {
            !!_pin ? setPin(_pin) : console.error(new Error("Could not generate pin"));
          });
      } else {
        DeviceRequests.handlePageErrorMessage({
          message: AccountErrorMessages.default,
          stack: { error: AccountErrorMessages.kss_handshake_undefined }
        });
      }
    },

    handleSuccess: async (data: RecoveryAccountCredentials): Promise<void> => {
      if (!!key_exchange_manager && !!data.message) {
        const decrypted = await key_exchange_manager.decryptMessage(data.message, protocol); // Decrypt the message
        const shard = Helpers.utf8Decode(Helpers.b64Decode(decrypted)); // Decode the message to raw shard
        handleAction({
          actionType: AccountUIActionTypes.PageSuccess,
          params: {
            approver_id: data.approver_id,
            approver_key_version: data.approver_key_version,
            approver_signature: data.approver_signature,
            new_shard: shard
          }
        });
      } else {
        DeviceRequests.handlePageErrorMessage({
          message: AccountErrorMessages.default,
          stack: { error: AccountErrorMessages.kss_success_undefined }
        });
      }
    },

    // Description: Reset page state and ws connection
    handleDestroy: () => {
      setStep(TransferAccountSteps.DISCONNECTED);
      dispatch(websocketActions.destroyWebsocket());
      handleAction({ actionType: ExpressResetPasswordUIActionTypes.Destroy });
    },

    handlePageErrorMessage: (params: { message: string, stack?: any, displayType?: MessageDisplayType }) => {
      const display_type = params.displayType || MessageHandlerDisplayType.toastr;
      dispatch(uiActions.handleRequestErrors(new Message(params.message, display_type), params.stack));
      setStep(TransferAccountSteps.ERROR);
    }
  };

  //  Description: Handle all actions from Children forms
  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((DeviceRequests as any)[callback] instanceof Function) {
      (DeviceRequests as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
      DeviceRequests.handlePageErrorMessage({ message, stack: action_obj, displayType: MessageHandlerDisplayType.logger });
    }
  }

  return <Row className="justify-content-center">
    <Col md={7}>
      <h2>Request Password Reset</h2>
      <Card className="pv-card">
        <ConnectAccountPanels />
      </Card>
    </Col>
  </Row>;
}

export default React.memo(GetShard);
